import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star, Check, Calendar, Brush, Shield, Factory, Hammer, Compass } from "lucide-react";
import { designs, testimonials, homeFaqs } from "../data/designs";
import { services } from "../data/services";
import DesignCard from "../components/DesignCard";
import Seo from "../components/Seo";
import { businessLd, websiteLd, faqLd } from "../components/StructuredData";

function useInView(threshold = 0.15) {
  const ref = useRef();
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setVisible(true);
      },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function AnimatedSection({ children, className = "", delay = 0 }) {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

const STAGE_ICONS = {
  consulting: <Compass size={22} />,
  design: <Brush size={22} />,
  build: <Hammer size={22} />,
};

export default function HomePage() {
  const [heroLoaded, setHeroLoaded] = useState(false);
  const [testimonialIdx, setTestimonialIdx] = useState(0);

  useEffect(() => {
    setHeroLoaded(true);
    const interval = setInterval(
      () => setTestimonialIdx((i) => (i + 1) % testimonials.length),
      5000
    );
    return () => clearInterval(interval);
  }, []);

  const featured = designs.filter((p) => p.featured).slice(0, 8);

  const whyUs = [
    {
      icon: <Shield size={22} />,
      title: "40+ years of expertise",
      desc: "Four decades inside Nepal's furniture industry — longer than most brands have existed.",
    },
    {
      icon: <Factory size={22} />,
      title: "Direct factory access",
      desc: "Personal relationships with the country's best workshops mean factory-direct pricing and zero showroom markup.",
    },
    {
      icon: <Brush size={22} />,
      title: "Bespoke by default",
      desc: "Nothing off-the-shelf. Every piece is sized, finished, and built to your space and your taste.",
    },
    {
      icon: <Check size={22} />,
      title: "End-to-end accountability",
      desc: "One person owns it all — consult, design, build, deliver, install. No handoffs, no excuses.",
    },
  ];

  const tickerItems = [
    "Free 30-min Consultation",
    "Bespoke Built to Your Space",
    "40+ Years of Expertise",
    "Factory-Direct Pricing — No Middlemen",
    "Serving All of Nepal",
    "WhatsApp Ananda for a Free Consultation",
  ];

  const jsonLd = [businessLd(), websiteLd(), faqLd(homeFaqs)];

  return (
    <>
      <Seo
        title="Interior Designer & Bespoke Furniture, Nepal"
        description="Bespoke interior design and custom furniture in Nepal. 40+ years of expertise. Free consultation, tailored design, factory-direct build — across Kathmandu Valley and beyond."
        path="/"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen">
        <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=1800&q=85"
              alt="Bespoke interior design and custom furniture by Ananda Paudel, Nepal"
              className="w-full h-full object-cover"
              loading="eager"
              fetchpriority="high"
              decoding="async"
              width="1800"
              height="1200"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-matte-900/80 via-matte-900/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-matte-900/40 to-transparent" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-8 w-full mt-14 sm:mt-16 lg:mt-20">
            <div className="max-w-xl lg:max-w-2xl">
              <div
                className={`transition-all duration-1000 delay-300 ${
                  heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
                }`}
              >
                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-[1.05] mb-4 sm:mb-5">
                  Consulting. Design. Build.
                  <br />
                  <em className="text-cream-300">For homes you'll grow into.</em>
                </h1>
                <p className="font-sans text-sm sm:text-base lg:text-lg text-cream-200 font-light leading-relaxed mb-5 sm:mb-6 lg:mb-7 max-w-md sm:max-w-lg">
                  40+ years of factory-direct experience, working one-on-one with you. From the
                  first 30-minute consultation to the day your pieces are
                  installed — Ananda owns every detail.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Link
                    to="/book"
                    className="bg-walnut-400 hover:bg-walnut-500 text-white font-sans text-xs sm:text-sm tracking-wider px-6 sm:px-8 py-3 sm:py-4 transition-colors duration-300 flex items-center gap-2 group"
                  >
                    <Calendar size={16} />
                    Book a Free Consultation
                    <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/services"
                    className="border border-cream-300 text-cream-100 hover:bg-white/10 font-sans text-xs sm:text-sm tracking-wider px-6 sm:px-8 py-3 sm:py-4 transition-colors duration-300"
                  >
                    How Ananda Works
                  </Link>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`hidden xl:flex absolute bottom-12 right-4 sm:right-8 gap-8 transition-all duration-1000 delay-700 ${
              heroLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
            }`}
          >
            {[
              ["40+", "Years Exp."],
              ["Factory", "Direct Access"],
              ["100%", "Bespoke Build"],
            ].map(([num, label]) => (
              <div key={label} className="text-center">
                <div className="font-serif text-2xl sm:text-3xl text-white">{num}</div>
                <div className="font-sans text-[10px] tracking-widest text-cream-400 uppercase">
                  {label}
                </div>
              </div>
            ))}
          </div>

          <div className="hidden xl:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 animate-bounce">
            <span className="font-sans text-[9px] tracking-widest text-cream-400 uppercase">
              Scroll
            </span>
            <div className="w-px h-10 bg-gradient-to-b from-cream-400 to-transparent" />
          </div>
        </section>

        <div className="bg-walnut-600 py-3 overflow-hidden">
          <div className="ticker-inner">
            {[...tickerItems, ...tickerItems].map((item, i) => (
              <span
                key={i}
                className="font-sans text-xs tracking-[0.2em] uppercase text-cream-100 mx-8 flex items-center gap-8 whitespace-nowrap"
              >
                {item}
                <span className="w-1 h-1 rounded-full bg-walnut-300 inline-block" />
              </span>
            ))}
          </div>
        </div>

        <section className="py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-8">
          <AnimatedSection className="text-center mb-14">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              How Ananda Works
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light leading-tight">
              Three stages. One craftsman.
            </h2>
            <p className="font-sans text-sm sm:text-base text-matte-500 max-w-2xl mx-auto mt-5 leading-relaxed">
              Every project — single piece or full home — moves through the
              same three stages. You see exactly where you are, every step.
            </p>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {services.map((s, i) => (
              <AnimatedSection key={s.slug} delay={i * 100}>
                <Link
                  to={`/services/${s.slug}`}
                  className="group block h-full p-8 border border-cream-200 hover:border-walnut-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm bg-white"
                >
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-11 h-11 rounded-full bg-walnut-100 text-walnut-500 flex items-center justify-center group-hover:bg-walnut-400 group-hover:text-white transition-all duration-300">
                      {STAGE_ICONS[s.slug]}
                    </div>
                    <span className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase">
                      Stage {s.stage}
                    </span>
                  </div>
                  <h3 className="font-serif text-2xl text-matte-900 mb-2">{s.title}</h3>
                  <p className="font-sans text-sm text-walnut-500 mb-4 italic">{s.headline}</p>
                  <p className="font-sans text-sm text-matte-500 leading-relaxed mb-5">{s.short}</p>
                  <span className="font-sans text-xs text-walnut-600 group-hover:text-walnut-800 transition-colors flex items-center gap-1">
                    Read more <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </AnimatedSection>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/book"
              className="inline-flex items-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wider px-8 py-4 hover:bg-walnut-700 transition-colors"
            >
              <Calendar size={16} />
              Start with a Free Consultation
            </Link>
          </div>
        </section>

        <section className="py-16 sm:py-24 bg-cream-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-8">
            <AnimatedSection className="flex items-end justify-between mb-12">
              <div>
                <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
                  Reference Designs
                </p>
                <h2 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
                  Past work — and starting points
                </h2>
                <p className="font-sans text-sm text-matte-500 mt-3 max-w-xl">
                  Save the pieces you love. Bring them to your free
                  consultation. Every design here is a starting point — final
                  pieces are built to your space.
                </p>
              </div>
              <Link
                to="/portfolio"
                className="hidden sm:flex items-center gap-2 font-sans text-sm text-walnut-500 hover:text-walnut-700 transition-colors group whitespace-nowrap"
              >
                View Full Portfolio
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </AnimatedSection>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
              {featured.map((p, i) => (
                <AnimatedSection key={p.id} delay={i * 60}>
                  <DesignCard design={p} />
                </AnimatedSection>
              ))}
            </div>

            <div className="text-center mt-10 sm:hidden">
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 font-sans text-sm text-walnut-500 border border-walnut-300 px-6 py-3 hover:bg-walnut-500 hover:text-white transition-colors"
              >
                View Full Portfolio
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </section>

        <section className="relative py-24 sm:py-36 overflow-hidden">
          <img
            src="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600&q=80"
            alt="Bespoke Nepali interior — Ananda Paudel project"
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
            decoding="async"
            width="1600"
            height="900"
          />
          <div className="absolute inset-0 bg-matte-900/65" />
          <AnimatedSection className="relative max-w-4xl mx-auto px-4 sm:px-8 text-center">
            <p className="font-sans text-[11px] tracking-[0.4em] text-walnut-300 uppercase mb-6">
              Ananda's Promise
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light leading-snug mb-8">
              "Four decades of expertise — directly, personally, honestly."
            </h2>
            <p className="font-sans text-base text-cream-300 max-w-xl mx-auto leading-relaxed mb-10">
              Ananda spent 40 years sourcing and designing Nepal's finest
              furniture for the country's biggest brands. Now he works for you
              — same factory contacts, same uncompromising eye for quality, no
              showroom markup.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 border border-cream-300 text-cream-100 font-sans text-sm tracking-wider px-8 py-4 hover:bg-white hover:text-matte-900 transition-colors duration-300"
            >
              Read Ananda's Story <ArrowRight size={14} />
            </Link>
          </AnimatedSection>
        </section>

        <section className="py-20 sm:py-28 max-w-7xl mx-auto px-4 sm:px-8">
          <AnimatedSection className="text-center mb-14">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              Why Work With Ananda
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
              Credentials a showroom can't match
            </h2>
          </AnimatedSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {whyUs.map((item, i) => (
              <AnimatedSection key={i} delay={i * 80}>
                <div className="group h-full p-7 border border-cream-200 hover:border-walnut-300 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 rounded-sm bg-white">
                  <div className="w-11 h-11 rounded-full bg-walnut-100 text-walnut-500 flex items-center justify-center mb-5 group-hover:bg-walnut-400 group-hover:text-white transition-all duration-300">
                    {item.icon}
                  </div>
                  <h3 className="font-serif text-lg text-matte-900 mb-2">{item.title}</h3>
                  <p className="font-sans text-sm text-matte-500 leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>

        <section className="py-20 sm:py-28 bg-matte-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-8">
            <AnimatedSection className="text-center mb-16">
              <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
                Reviews
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl text-white font-light">
                Trusted by homeowners
                <br />
                across Nepal
              </h2>
            </AnimatedSection>

            <div className="relative">
              <div className="overflow-hidden">
                <div
                  className="flex transition-transform duration-700 ease-in-out"
                  style={{ transform: `translateX(-${testimonialIdx * 100}%)` }}
                >
                  {testimonials.map((t) => (
                    <div key={t.id} className="min-w-full px-4 sm:px-12 text-center">
                      <div className="flex justify-center gap-1 mb-8">
                        {[...Array(t.rating)].map((_, i) => (
                          <Star key={i} size={16} className="text-walnut-400 fill-walnut-400" />
                        ))}
                      </div>
                      <blockquote className="font-serif text-xl sm:text-2xl lg:text-3xl text-cream-200 font-light leading-relaxed mb-8 italic">
                        "{t.text}"
                      </blockquote>
                      <div className="flex items-center justify-center gap-4">
                        <img
                          src={t.avatar}
                          alt={`${t.name}, ${t.role}`}
                          className="w-12 h-12 rounded-full object-cover border-2 border-walnut-500"
                          loading="lazy"
                          decoding="async"
                          width="48"
                          height="48"
                        />
                        <div className="text-left">
                          <p className="font-sans text-sm font-medium text-white">{t.name}</p>
                          <p className="font-sans text-xs text-matte-400">
                            {t.role} · {t.location}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-center gap-2 mt-10">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setTestimonialIdx(i)}
                    aria-label={`Show testimonial ${i + 1}`}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      i === testimonialIdx ? "w-8 bg-walnut-400" : "w-2 bg-matte-600"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 sm:py-28 max-w-3xl mx-auto px-4 sm:px-8">
          <AnimatedSection className="text-center mb-14">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              Common Questions
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light">
              Things people ask before booking
            </h2>
          </AnimatedSection>
          <div className="space-y-4">
            {homeFaqs.map((f, i) => (
              <AnimatedSection key={f.q} delay={i * 60}>
                <details className="group border border-cream-200 rounded-sm bg-white">
                  <summary className="cursor-pointer list-none p-5 font-serif text-base sm:text-lg text-matte-900 flex items-center justify-between gap-4">
                    {f.q}
                    <span className="text-walnut-500 group-open:rotate-45 transition-transform text-2xl flex-shrink-0">
                      +
                    </span>
                  </summary>
                  <p className="font-sans text-sm text-matte-500 leading-relaxed px-5 pb-5">
                    {f.a}
                  </p>
                </details>
              </AnimatedSection>
            ))}
          </div>
        </section>

        <section className="py-20 sm:py-28 bg-cream-100">
          <AnimatedSection className="max-w-3xl mx-auto px-4 sm:px-8 text-center">
            <p className="font-sans text-xs tracking-[0.3em] text-walnut-400 uppercase mb-3">
              Ready when you are
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl text-matte-900 font-light mb-5">
              Start with a free consultation
            </h2>
            <p className="font-sans text-sm sm:text-base text-matte-500 leading-relaxed mb-10 max-w-xl mx-auto">
              30 minutes, no obligation. Tell Ananda about your space —
              he'll tell you, plainly, what's worth doing.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/book"
                className="inline-flex items-center gap-2 bg-matte-900 text-cream-100 font-sans text-sm tracking-wider px-10 py-4 hover:bg-walnut-700 transition-colors"
              >
                <Calendar size={16} />
                Book Your Consultation
              </Link>
              <Link
                to="/portfolio"
                className="inline-flex items-center gap-2 border border-walnut-300 text-walnut-600 font-sans text-sm tracking-wider px-10 py-4 hover:bg-walnut-500 hover:text-white hover:border-walnut-500 transition-colors"
              >
                Browse the Portfolio
                <ArrowRight size={14} />
              </Link>
            </div>
          </AnimatedSection>
        </section>
      </div>
    </>
  );
}
